import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View, Alert } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { Button as Button95, Hourglass, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { UserInfo } from "@/types/user_info";
import { RelativePathString, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setLoggedIn, setData } from '@/state/user';
import { Meta } from '@/components/Meta';
import { Image } from 'expo-image';
import { PasswordShowHideButton } from '@/components/PasswordShowHideButton';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  inputWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
});

export default function Login() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const emailInput = useRef<RNTextInput>(null);
  const passwordInput = useRef<RNTextInput>(null);
  const router = useRouter();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const dispatch = useDispatch();
  const [passwordShown, setPasswordShown] = useState(false);

  const { register, control, handleSubmit, setValue, reset, getValues, formState: { errors } } = useForm({
    defaultValues: {
      email: '',
      password: '',
    }
  });

  const onSubmit = useCallback(async (data: any) => {
    try {
      showSpinner();

      const response = await AppUtilities.fetchUnauthorized(
        "Login.onSubmit",
        "/rabbl/api/login",
        "POST",
        {
          domain: domain,
          email: data.email,
          password: data.password,
        }
      );
      AppUtilities.login((response as any).body);

      const membership: CommunityMembership | null = ((response as any).body as any).community_membership;
      if (membership) {
        AppUtilities.info("community login has membership");
        dispatch(setData(response));
        router.replace(AppUtils.getProfileCreateNextPageLink(membership) as RelativePathString);
      } else {
        AppUtilities.info("community login no membership");
        dispatch(setLoggedIn(true));
        dispatch(setData(response));
        router.replace(AppConsts.SUBDOMAIN_HOMEPAGE_LOGGED_IN_UNJOINED as RelativePathString);
      }

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
      AppUtilities.handleException(e, "Login.onSubmit");
    } finally {
      hideSpinner();
    }
  }, [domain]);

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (emailInput && emailInput.current) {
        emailInput.current?.focus();
      }
    }
  }, [emailInput]);

  const onEmailSubmit = useCallback(() => {
    const emailValue = getValues('email');
    if (emailValue && emailValue.length > 0) {
      const passwordValue = getValues('password');
      if (passwordValue && passwordValue.length > 0) {
        passwordInput.current?.focus();
        //handleSubmit(onSubmit, onValidationErrors);
      } else {
        passwordInput.current?.focus();
      }
    }
  }, []);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Login"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>

          <Controller
            control={control}
            name="email"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={AppConstants.STYLES.passwordInputRow}>
                <TextInput
                  ref={emailInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="Email"
                  style={styles.formElement}
                  returnKeyType="next"
                  onBlur={onBlur}
                  autoComplete="email"
                  keyboardType="email-address"
                  textContentType="emailAddress"
                  onSubmitEditing={onEmailSubmit}
                  autoCapitalize='none'
                />
                {AppUtils.SHOW_PASSWORD_BLANK}
              </View>
            )}
          />
          {errors.email && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ Email is required. ^</AppText>}

          <Controller
            control={control}
            name="password"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={AppConstants.STYLES.passwordInputRow}>
                <TextInput
                  ref={passwordInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="Password"
                  style={styles.formElement}
                  returnKeyType="go"
                  onBlur={onBlur}
                  autoComplete="password"
                  textContentType="password"
                  onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                  secureTextEntry={passwordShown ? false : true}
                />
                <PasswordShowHideButton passwordShown={passwordShown} setPasswordShown={setPasswordShown} />
              </View>
            )}
          />
          {errors.password && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ Password is required. ^</AppText>}

          <View style={styles.inputWrapper}>
            <Button95 onPress={handleSubmit(onSubmit, onValidationErrors)}>Login</Button95>
          </View>
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
